/*
  Cart drawer (Distl) for The Chart & Map Shop.
  - Product forms add to cart over AJAX and open the drawer instead of
    redirecting to /cart. Works with the EPA add-ons app, which adds the
    finish first and then calls form.submit() for the main product.
  - Header cart links open the drawer (normal link on the /cart page).
  - Quantity / remove use /cart/change.js and the Section Rendering API (header section).
  - Any other app that changes the cart via fetch or XHR triggers a refresh.
  - "You might also like" uses Shopify's product recommendations (related).
  - Order note (gift message) saves to the cart note via /cart/update.js.
  - If anything fails, the form falls back to a normal submit.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-cart-drawer]');
  if (!root || root.getAttribute('data-enabled') === 'false') return;
  // The drawer is rendered inside the header section; move it to <body> so
  // header stacking/overflow rules can't clip it.
  document.body.appendChild(root);

  var SECTION = 'header';
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var CART_URL = ROOT + 'cart';
  var onCartPage = /\/cart\/?$/.test(location.pathname);
  var nativeFetch = window.fetch.bind(window);
  var nativeSubmit = HTMLFormElement.prototype.submit;
  var lastFocus = null;
  var refreshTimer = null;
  var OWN = 'X-Cart-Drawer';
  var RECS_SHOWN = 3;
  var recsCache = {};
  var added = null;        // { text, until } - "added to cart" message, survives re-renders
  var addedTimer = null;
  var giftOpen = null;     // null = follow the server (open when a note exists)
  var noteDraft = null;    // unsaved note text, survives re-renders
  var noteTimer = null;

  /* ---------- helpers ---------- */

  function inner() { return root.querySelector('[data-cart-drawer-inner]'); }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    try { return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }); }
    catch (e) { return '$' + n.toFixed(2); }
  }

  function sized(url, w) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function isProductForm(form) {
    if (!form || form.nodeName !== 'FORM') return false;
    if (form.hasAttribute('data-no-cart-drawer') || root.contains(form)) return false;
    var action = form.getAttribute('action') || '';
    return /\/cart\/add/.test(action) && (form.method || '').toLowerCase() === 'post';
  }

  function isCartPath(url) {
    try {
      var p = new URL(url, location.href);
      return p.origin === location.origin && /\/cart\/?$/.test(p.pathname) && !p.search;
    } catch (e) { return false; }
  }

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && input.url) return input.url;
    return String(input || '');
  }

  function isCartMutation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
  }

  /* ---------- open / close ---------- */

  function open() {
    if (root.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-lock');
    setTimeout(function () {
      var c = root.querySelector('.cart-drawer__close');
      if (c) c.focus({ preventScroll: true });
    }, 60);
  }

  function close() {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---------- "added to cart" message ---------- */

  function paintAdded() {
    var box = root.querySelector('[data-cart-drawer-added]');
    if (!box) return;
    if (!added || Date.now() > added.until) { box.hidden = true; return; }
    box.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="currentColor"/><path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg><span>' + esc(added.text) + '</span>';
    box.hidden = false;
  }

  function showAdded(title) {
    added = { text: title ? title + ' added to your cart' : 'Added to your cart', until: Date.now() + 8000 };
    paintAdded();
    clearTimeout(addedTimer);
    addedTimer = setTimeout(function () { added = null; paintAdded(); }, 8000);
  }

  function decode(s) {
    var t = document.createElement('textarea');
    t.innerHTML = s || '';
    return t.value;
  }

  function addedTitle(data) {
    if (!data) return '';
    if (data.product_title) return decode(data.product_title);
    if (data.items && data.items.length) return decode(data.items[data.items.length - 1].product_title);
    return '';
  }

  /* ---------- rendering ---------- */

  function updateHeader() {
    var el = inner();
    if (!el) return;
    var count = parseInt(el.getAttribute('data-cart-count'), 10) || 0;
    var label = el.getAttribute('data-cart-label');
    root.classList.toggle('has-items', count > 0);
    document.querySelectorAll('[data-cart-header-label]').forEach(function (n) { n.textContent = label; });
    document.querySelectorAll('[data-cart-bubble]').forEach(function (n) {
      n.textContent = count;
      n.hidden = count === 0;
    });
  }

  function afterRender() {
    updateHeader();
    paintAdded();
    restoreGift();
    loadRecs();
  }

  function render(sections) {
    var html = sections && sections[SECTION];
    if (!html) return refresh();
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-cart-drawer-inner]');
    var current = inner();
    if (!fresh || !current) return;
    var scroller = current.querySelector('.cart-drawer__scroll');
    var scrollTop = scroller ? scroller.scrollTop : 0;
    current.replaceWith(fresh);
    var nextScroller = fresh.querySelector('.cart-drawer__scroll');
    if (nextScroller) nextScroller.scrollTop = scrollTop;
    afterRender();
    root.classList.remove('is-busy');
    document.dispatchEvent(new CustomEvent('cart-drawer:updated'));
  }

  function refresh() {
    return nativeFetch(CART_URL + '?sections=' + SECTION, { headers: { 'X-Cart-Drawer': '1' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(render)
      .catch(function () { root.classList.remove('is-busy'); });
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 150);
  }

  function showDrawerError(msg) {
    var box = root.querySelector('[data-cart-drawer-error]');
    if (!box) return;
    box.textContent = msg;
    box.hidden = false;
  }

  /* ---------- recommendations ---------- */

  function fetchRecs(pid) {
    if (!recsCache[pid]) {
      recsCache[pid] = nativeFetch(ROOT + 'recommendations/products.json?product_id=' + encodeURIComponent(pid) + '&limit=10&intent=related', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) { return (d && d.products) || []; })
        .catch(function () { delete recsCache[pid]; return []; });
    }
    return recsCache[pid];
  }

  function recItem(p) {
    var variant = null;
    (p.variants || []).some(function (v) { if (v.available) { variant = v; return true; } return false; });
    var single = (p.variants || []).length === 1;
    var img = p.featured_image ? '<img src="' + esc(sized(p.featured_image, 160)) + '" alt="" width="56" height="56" loading="lazy">' : '';
    var compare = p.compare_at_price && p.compare_at_price > p.price ? '<s>' + money(p.compare_at_price) + '</s> ' : '';
    var action = single && variant
      ? '<button type="button" class="cart-drawer__rec-add" data-rec-add="' + variant.id + '" data-rec-title="' + esc(p.title) + '" aria-label="Add ' + esc(p.title) + ' to cart">+ Add</button>'
      : '<a class="cart-drawer__rec-add" href="' + esc(p.url) + '">Options</a>';
    return '<li class="cart-drawer__rec">' +
      '<a class="cart-drawer__rec-image" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
      '<div class="cart-drawer__rec-info"><a class="cart-drawer__rec-title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
      '<span class="cart-drawer__rec-price">' + compare + money(p.price) + '</span></div>' +
      action + '</li>';
  }

  function loadRecs() {
    var el = inner();
    var box = el && el.querySelector('[data-cart-recs]');
    if (!box) return;
    var pid = el.getAttribute('data-recs-product');
    if (!pid) return;
    var inCart = (el.getAttribute('data-cart-products') || '').split(',');
    fetchRecs(pid).then(function (list) {
      if (!root.contains(box)) return; // drawer re-rendered in the meantime
      var picks = list.filter(function (p) {
        return p.available && inCart.indexOf(String(p.id)) === -1;
      }).slice(0, RECS_SHOWN);
      if (!picks.length) { box.hidden = true; return; }
      box.querySelector('[data-cart-recs-list]').innerHTML = picks.map(recItem).join('');
      box.hidden = false;
    });
  }

  function addRec(btn) {
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    var title = btn.getAttribute('data-rec-title');
    nativeFetch(ROOT + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Cart-Drawer': '1' },
      body: JSON.stringify({ items: [{ id: Number(btn.getAttribute('data-rec-add')), quantity: 1 }], sections: SECTION, sections_url: location.pathname })
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (r) {
        if (!r.ok || r.data.status) {
          btn.classList.remove('is-loading');
          showDrawerError(r.data.description || r.data.message || 'Sorry, that could not be added.');
          return;
        }
        showAdded(title);
        render(r.data.sections);
        document.dispatchEvent(new CustomEvent('cart-drawer:added', { detail: r.data }));
      })
      .catch(function () { btn.classList.remove('is-loading'); refresh(); });
  }

  /* ---------- order note ---------- */

  function restoreGift() {
    var details = root.querySelector('[data-cart-gift]');
    if (!details) return;
    if (giftOpen !== null) details.open = giftOpen;
    var ta = details.querySelector('[data-cart-note]');
    if (ta && noteDraft !== null) ta.value = noteDraft;
  }

  function noteStatus(text) {
    var s = root.querySelector('[data-cart-note-status]');
    if (s) s.textContent = text;
  }

  function saveNote() {
    if (noteDraft === null) return;
    var value = noteDraft;
    noteStatus('Saving…');
    nativeFetch(ROOT + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Cart-Drawer': '1' },
      body: JSON.stringify({ note: value })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('note');
        if (noteDraft === value) noteDraft = null;
        noteStatus(value ? 'Note saved. It will go with your order.' : 'Your note is saved with your order.');
      })
      .catch(function () { noteStatus('Could not save your note. Please try again.'); });
  }

  root.addEventListener('toggle', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-cart-gift]')) {
      giftOpen = e.target.open;
    }
  }, true);

  root.addEventListener('input', function (e) {
    if (!e.target.matches('[data-cart-note]')) return;
    noteDraft = e.target.value;
    noteStatus('…');
    clearTimeout(noteTimer);
    noteTimer = setTimeout(saveNote, 700);
  });

  root.addEventListener('focusout', function (e) {
    if (e.target.matches && e.target.matches('[data-cart-note]') && noteDraft !== null) {
      clearTimeout(noteTimer);
      saveNote();
    }
  });

  /* ---------- add to cart ---------- */

  function formError(form, msg) {
    var box = form.querySelector('.cart-drawer-form-error');
    if (!box) {
      box = document.createElement('p');
      box.className = 'cart-drawer-form-error';
      box.setAttribute('role', 'alert');
      var btn = form.querySelector('[name="add"], [type="submit"]');
      (btn && btn.parentNode ? btn.parentNode : form).insertBefore(box, btn ? btn.nextSibling : null);
    }
    box.textContent = msg || '';
    box.hidden = !msg;
  }

  function addFromForm(form, submitter) {
    var btn = submitter || form.querySelector('[name="add"]') || form.querySelector('[type="submit"]');
    if (btn && btn.classList.contains('is-loading')) return;
    if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
    formError(form, '');

    var body = new FormData(form);
    body.append('sections', SECTION);
    body.append('sections_url', location.pathname);

    nativeFetch(ROOT + 'cart/add.js', {
      method: 'POST',
      body: body,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json', 'X-Cart-Drawer': '1' }
    })
      .then(function (res) {
        return res.json().then(function (data) { return { ok: res.ok, data: data }; });
      })
      .then(function (r) {
        if (!r.ok || r.data.status) {
          formError(form, r.data.description || r.data.message || 'Sorry, that could not be added to your cart.');
          return;
        }
        showAdded(addedTitle(r.data));
        render(r.data.sections);
        open();
        document.dispatchEvent(new CustomEvent('cart-drawer:added', { detail: r.data }));
      })
      .catch(function () {
        // Network or parsing problem: fall back to the normal cart page flow.
        nativeSubmit.call(form);
      })
      .then(function () {
        if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
      });
  }

  // Normal submits (button click, Enter key). Runs after any listener on the
  // form itself, so an app that has already handled the submit is left alone.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (e.defaultPrevented || !isProductForm(form)) return;
    e.preventDefault();
    addFromForm(form, e.submitter);
  });

  // Programmatic form.submit() (used by the EPA add-ons app) skips the submit
  // event, so route product forms through the drawer as well.
  HTMLFormElement.prototype.submit = function () {
    if (isProductForm(this)) return addFromForm(this);
    return nativeSubmit.call(this);
  };

  /* ---------- cart changes made by other scripts ---------- */

  window.fetch = function (input, init) {
    var url = urlOf(input);
    var own = init && init.headers && (init.headers[OWN] || (init.headers.get && init.headers.get(OWN)));
    var p = nativeFetch(input, init);
    if (!own && isCartMutation(url)) {
      p.then(function (res) {
        if (!res.ok) return;
        scheduleRefresh();
        if (/\/cart\/add/.test(url) && !onCartPage) open();
      }).catch(function () {});
    }
    return p;
  };

  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    var u = String(url || '');
    if (isCartMutation(u)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) {
          scheduleRefresh();
          if (/\/cart\/add/.test(u) && !onCartPage) open();
        }
      });
    }
    return xhrOpen.apply(this, arguments);
  };

  /* ---------- quantity / remove ---------- */

  function changeLine(key, qty) {
    if (qty < 0 || isNaN(qty)) qty = 0;
    root.classList.add('is-busy');
    nativeFetch(ROOT + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Cart-Drawer': '1' },
      body: JSON.stringify({ id: key, quantity: qty, sections: SECTION, sections_url: location.pathname })
    })
      .then(function (res) {
        return res.json().then(function (data) { return { ok: res.ok, data: data }; });
      })
      .then(function (r) {
        if (!r.ok || r.data.status) {
          var msg = r.data.description || r.data.message || 'Sorry, that quantity is not available.';
          return refresh().then(function () { showDrawerError(msg); });
        }
        render(r.data.sections);
      })
      .catch(function () { refresh(); });
  }

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-drawer-close]')) {
      var a = e.target.closest('a');
      if (!a) e.preventDefault();
      close();
      return;
    }
    var rec = e.target.closest('[data-rec-add]');
    if (rec) { addRec(rec); return; }
    var btn = e.target.closest('[data-qty-change]');
    if (btn) {
      var item = btn.closest('[data-key]');
      changeLine(item.getAttribute('data-key'), parseInt(btn.getAttribute('data-qty-change'), 10));
    }
  });

  root.addEventListener('change', function (e) {
    var input = e.target.closest('[data-qty-input]');
    if (!input) return;
    var item = input.closest('[data-key]');
    changeLine(item.getAttribute('data-key'), parseInt(input.value, 10));
  });

  root.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    var f = Array.prototype.filter.call(
      root.querySelectorAll('.cart-drawer__panel a[href], .cart-drawer__panel button:not([disabled]), .cart-drawer__panel input, .cart-drawer__panel textarea, .cart-drawer__panel summary'),
      function (n) { return n.offsetParent !== null && n.getAttribute('tabindex') !== '-1'; }
    );
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------- header cart links ---------- */

  document.addEventListener('click', function (e) {
    if (onCartPage || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a || root.contains(a) || !isCartPath(a.href)) return;
    e.preventDefault();
    open();
  });

  /* ---------- keep in sync ---------- */

  document.addEventListener('cart:refresh', refresh);
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });

  window.CartDrawer = { open: open, close: close, refresh: refresh };
  afterRender();
})();
